import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Card, Box, CardContent, Grid, Button, Stack, Typography, Divider, Slider, useTheme } from '@mui/material'
import { useFormContext, Controller } from 'react-hook-form'
import { shallowEqual, useSelector } from 'react-redux'
import T from 'T'
import { FilterMultiSelectField } from 'components/configured-service/CreateDraftConfiguredServiceDialog/filters'
import { CSD_MARKER_TYPE, selectCSDMapMarkerType } from 'slices/configuredService/createDraftCSDSlice'
import { SCHEDULING_MAP_SURFACE, selectSchedulingResultsMeta } from 'slices/configuredService/configuredServiceScheduleSlice'
import OverCapacityNotice from 'components/configured-service/Schedule/OverCapacityNotice'
import { Z_INDEX } from 'theme/zIndex'
import { startOfWeek, endOfWeek } from 'date-fns'
import { filterDefaultValues } from 'components/configured-service/CreateDraftConfiguredServiceDialog/filterSettings'
import useRadiusUnit from 'components/configured-service/Schedule/hooks/useRadiusUnit'

import DaysOfWeekLegends from 'components/configured-service/Schedule/DaysOfWeekLegends'
import RoutesLegends from 'components/configured-service/Schedule/RoutesLegends'

const SchedulingFiltersPanel = ({
  businessLineOptions,
  materialOptions,
  measureOptions,
  serviceActionOptions,
  distanceUnit = 'mi',
  radiusMinMeters = 10000, // ~10 km
  radiusMaxMeters = 50000, // ~50 km
  radiusStepMeters = 5000, // ~5 km
}) => {
  const { control, setValue } = useFormContext()
  const theme = useTheme()
  const mapMarkerType = useSelector(selectCSDMapMarkerType, shallowEqual)
  const { truncated, totalMatched, cap } = useSelector(state => selectSchedulingResultsMeta(state, SCHEDULING_MAP_SURFACE.NEW_SERVICE_SCHEDULE), shallowEqual)

  const isMarkerTypeWeekDay = useMemo(() => mapMarkerType === CSD_MARKER_TYPE.WEEK_DAY, [mapMarkerType])
  const isMarkerTypeRoute = useMemo(() => mapMarkerType === CSD_MARKER_TYPE.ROUTE, [mapMarkerType])

  const { unitLabel, sliderConfig, displayUnitValue, onSliderChange, onSliderCommit, resetRadius } = useRadiusUnit({
    distanceUnitProp: distanceUnit,
    radiusMinMeters,
    radiusMaxMeters,
    radiusStepMeters,
  })

  const handleClearAll = () => {
    setValue('measure', [])
    setValue('material', [])
    setValue('action', [])
    setValue('businessLine', [])
    resetRadius()
    const base = startOfWeek(new Date(), { weekStartsOn: 0 })
    setValue('startDate', base)
    setValue('endDate', endOfWeek(base, { weekStartsOn: 0 }))
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    days.forEach(day => {
      setValue(`selectedDays.${day}`, true)
    })
  }

  const slotPropsPopper = {
    popper: {
      sx: {
        zIndex: Z_INDEX.MENU(theme),
      },
    },
  }

  return (
    <Card variant="outlined" sx={{ display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <CardContent sx={{ flex: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography color="textSecondary" variant="h5" gutterBottom>
            Filter options
          </Typography>
          <Button onClick={handleClearAll}>{T.RESET_ALL}</Button>
        </Stack>
        <Box pb={2} pt={1}>
          <Divider />
        </Box>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Box sx={{ mt: 2, px: 2 }}>
              <Typography gutterBottom>
                Radius ({unitLabel}): {displayUnitValue}
              </Typography>
              <Controller
                name="radius"
                control={control}
                defaultValue={filterDefaultValues.radius}
                render={() => {
                  const handleSliderChangeCommitted = (event, val) => onSliderCommit(event, val)
                  const handleSliderChange = (event, val) => onSliderChange(event, val)
                  return (
                    <Slider
                      value={displayUnitValue}
                      onChange={handleSliderChange}
                      onChangeCommitted={handleSliderChangeCommitted}
                      min={sliderConfig.min}
                      max={sliderConfig.max}
                      step={sliderConfig.step}
                      shiftStep={sliderConfig.step}
                      marks
                      valueLabelDisplay="auto"
                      aria-labelledby="radius-slider"
                    />
                  )
                }}
              />
            </Box>
          </Grid>
          {truncated && (
            <Grid size={12}>
              <Box px={2}>
                <OverCapacityNotice variant="panel" truncated={truncated} totalMatched={totalMatched} cap={cap} />
              </Box>
            </Grid>
          )}
          {isMarkerTypeWeekDay && <DaysOfWeekLegends />}
          {isMarkerTypeRoute && <RoutesLegends />}
          <Grid size={12}>
            <FilterMultiSelectField
              deprecatedLabel={false}
              name="measure"
              control={control}
              label="Measure"
              options={measureOptions}
              getOptionLabel={option => option.label}
              fullWidth
              slotProps={slotPropsPopper}
            />
          </Grid>

          <Grid size={12}>
            <FilterMultiSelectField
              deprecatedLabel={false}
              name="material"
              control={control}
              label="Material"
              options={materialOptions}
              getOptionLabel={option => option.label}
              fullWidth
              slotProps={slotPropsPopper}
            />
          </Grid>

          <Grid size={12}>
            <FilterMultiSelectField
              deprecatedLabel={false}
              name="action"
              control={control}
              label="Action"
              options={serviceActionOptions}
              getOptionLabel={option => option.label}
              fullWidth
              slotProps={slotPropsPopper}
            />
          </Grid>

          <Grid size={12}>
            <FilterMultiSelectField
              deprecatedLabel={false}
              name="businessLine"
              control={control}
              label="Business Line"
              options={businessLineOptions}
              getOptionLabel={option => option.label}
              fullWidth
              slotProps={slotPropsPopper}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

SchedulingFiltersPanel.propTypes = {
  businessLineOptions: PropTypes.array,
  materialOptions: PropTypes.array,
  measureOptions: PropTypes.array,
  serviceActionOptions: PropTypes.array,
  distanceUnit: PropTypes.oneOf(['mi', 'km']),
  radiusMinMeters: PropTypes.number,
  radiusMaxMeters: PropTypes.number,
  radiusStepMeters: PropTypes.number,
}

export default React.memo(SchedulingFiltersPanel)
